import { Decimal, dec, lineAmount, lineQty, markupPctFromPrices, qty, toQtyString, wholesaleFromMarkup } from '@app/shared';
import type { Qty } from '@app/shared';
import { sumMoney } from '@/shared/lib/money';
import { describeWarnings } from '@/shared/lib/warnings';
import type { WarningEntry } from '@/shared/lib/warnings';
import type { CategoryDto } from '@/entities/category';
import type {
  DocumentDto,
  DocumentLineDto,
  DocumentLineInput,
  DuplicateTemplateDto,
  LineStock,
  ReturnTemplateDto,
  TemplateLineCategory,
  WriteOffReason,
} from '@/entities/document';

export type EditorDocumentType = 'PURCHASE' | 'SALE' | 'RETURN_IN' | 'RETURN_OUT' | 'WRITE_OFF' | 'INVENTORY';

export interface EditorLine {
  key: string;
  /** id сохранённой строки — по нему сервер узнаёт строку при правке (номер строки меняется при удалении соседних). */
  id?: string;
  lineNo: number;
  categoryId?: string;
  categoryName?: string;
  categoryPcsPerBox?: Qty | null;
  /** §10, §12.6: товар строки в архиве — строка подсвечивается, провести документ нельзя. */
  archived?: boolean;
  stock?: LineStock;
  /** Закупочная цена последнего проведённого прихода товара ("12.50"); `null`/нет — ориентира нет. */
  lastPurchasePrice?: string | null;
  boxes: number | null;
  /** P строки — штук в коробке, каноническая строка ("4.3"): дробь не проходит через float. */
  pcsPerBox: Qty | null;
  unitPrice: string | null;
  wholesalePrice: string | null;
  markupPct: string | null;
  lastEdited: 'wholesale' | 'markup';
  isNew: boolean;
  isPromo: boolean;
  expiryDate: string | null;
  unitCost: string | null;
  costAmount: string | null;
  costIsProvisional: boolean;
  /** RETURN_IN/RETURN_OUT (§11.2/§11.3): строка-основание и предзаполненный контекст. */
  baseLineId?: string;
  soldQty?: Qty;
  alreadyReturnedQty?: Qty;
  qtyOverBase?: Qty;
  /** WRITE_OFF (§11.4). */
  reason?: WriteOffReason | null;
  batchId?: string | null;
  /** INVENTORY (§6.1 [ИЗМ]): факт — целые коробки/россыпь. */
  countedBoxes?: number | null;
  countedLoose?: Qty | null;
}

/** Канонический вид количества; пусто остаётся пустым. Принимает и число (ответ старого сервера). */
function normQty(value: Qty | number | null | undefined): Qty | null {
  return value === null || value === undefined ? null : toQtyString(value);
}

function stockOf(stock: LineStock | null | undefined): LineStock | undefined {
  return stock ? { sealedBoxes: stock.sealedBoxes, loosePcs: toQtyString(stock.loosePcs), qtyTotal: toQtyString(stock.qtyTotal) } : undefined;
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `l${keySeq}-${Date.now()}`;
}

export function emptyLine(lineNo: number): EditorLine {
  return {
    key: nextKey(),
    lineNo,
    boxes: null,
    pcsPerBox: null,
    unitPrice: null,
    wholesalePrice: null,
    markupPct: null,
    lastEdited: 'wholesale',
    isNew: false,
    isPromo: false,
    expiryDate: null,
    unitCost: null,
    costAmount: null,
    costIsProvisional: false,
    reason: null,
    batchId: null,
    countedBoxes: null,
    countedLoose: null,
  };
}

/** `withStock: false` — для проведённой инвентаризации: текущий остаток уже включает её саму, и
 * «Учёт»/«Разница» от него показали бы не то, что она исправила. Вместо него берётся остаток по
 * учёту на момент проведения (`accounted`) — его документ помнит сам. */
export function fromDocumentLine(dto: DocumentLineDto, withStock = true): EditorLine {
  return {
    key: nextKey(),
    id: dto.id,
    lineNo: dto.lineNo,
    categoryId: dto.categoryId ?? undefined,
    categoryName: dto.category?.name,
    categoryPcsPerBox: dto.category?.pcsPerBox,
    archived: dto.category?.isArchived ?? false,
    stock: stockOf(withStock ? dto.category?.stock : dto.accounted),
    lastPurchasePrice: dto.category?.lastPurchasePrice ?? null,
    boxes: dto.boxes,
    pcsPerBox: normQty(dto.pcsPerBox),
    unitPrice: dto.unitPrice,
    wholesalePrice: dto.wholesalePrice,
    markupPct: dto.markupPct,
    lastEdited: 'wholesale',
    isNew: dto.isNew,
    isPromo: dto.isPromo,
    expiryDate: dto.expiryDate,
    unitCost: dto.unitCost,
    costAmount: dto.costAmount,
    costIsProvisional: dto.costIsProvisional,
    baseLineId: dto.baseLineId ?? undefined,
    qtyOverBase: normQty(dto.qtyOverBase) ?? undefined,
    reason: dto.reason,
    batchId: dto.batchId,
    countedBoxes: dto.countedBoxes,
    countedLoose: normQty(dto.countedLoose),
  };
}

/** §11.2/§11.3 «Создать возврат»: шаблон из проведённой продажи/прихода — ничего не сохраняет. */
export function fromReturnTemplate(template: ReturnTemplateDto): EditorLine[] {
  if (template.lines.length === 0) return [emptyLine(1)];
  const lines = template.lines.map((l) => ({
    key: nextKey(),
    lineNo: l.lineNo,
    categoryId: l.categoryId ?? undefined,
    ...templateCategory(l),
    boxes: l.boxes,
    pcsPerBox: normQty(l.pcsPerBox),
    unitPrice: l.unitPrice,
    wholesalePrice: null,
    markupPct: null,
    lastEdited: 'wholesale' as const,
    isNew: false,
    isPromo: false,
    expiryDate: null,
    unitCost: null,
    costAmount: null,
    costIsProvisional: false,
    baseLineId: l.baseLineId,
    soldQty: toQtyString(l.soldQty),
    alreadyReturnedQty: toQtyString(l.alreadyReturnedQty),
    reason: null,
    batchId: null,
  }));
  return [...lines, emptyLine(lines.length + 1)];
}

/** §12.6 «Дублировать»: шаблон из любого документа — ничего не сохраняет. Строка с архивной
 * категорией переносится как есть и подсвечивается; сервер заблокирует проведение (§8.1
 * CATEGORY_ARCHIVED), пока категорию не заменят вручную. */
export function fromDuplicateTemplate(template: DuplicateTemplateDto): EditorLine[] {
  if (template.lines.length === 0) return [emptyLine(1)];
  const lines = template.lines.map((l) => ({
    key: nextKey(),
    lineNo: l.lineNo,
    categoryId: l.categoryId ?? undefined,
    ...templateCategory(l),
    boxes: l.boxes,
    pcsPerBox: normQty(l.pcsPerBox),
    unitPrice: l.unitPrice,
    wholesalePrice: l.wholesalePrice,
    markupPct: l.markupPct,
    lastEdited: 'wholesale' as const,
    isNew: l.isNew,
    isPromo: l.isPromo,
    expiryDate: null,
    unitCost: null,
    costAmount: null,
    costIsProvisional: false,
    reason: null,
    batchId: null,
  }));
  return [...lines, emptyLine(lines.length + 1)];
}

function templateCategory(line: TemplateLineCategory): Pick<EditorLine, 'categoryName' | 'categoryPcsPerBox' | 'archived' | 'stock' | 'lastPurchasePrice'> {
  return {
    categoryName: line.categoryName ?? undefined,
    categoryPcsPerBox: line.categoryPcsPerBox,
    archived: line.archived,
    stock: stockOf(line.stock),
    lastPurchasePrice: line.lastPurchasePrice ?? null,
  };
}

/** Количество и сумма — производные, всегда пересчитываются (§4, §5). Количество — Decimal:
 * `3 × 4.3 = 12.9`, а не 12.899999999999999. */
export function qtyOf(line: EditorLine): Decimal {
  // Пустое «Штук в коробке» (продажа, §5) — целая коробка, т.е. N товара.
  return lineQty(line.boxes, line.pcsPerBox ?? line.categoryPcsPerBox);
}

export function amountOf(line: EditorLine): string {
  return lineAmount(qtyOf(line), line.unitPrice ?? undefined).toFixed(2);
}

/** §5: цена продажи ниже закупочной цены последнего прихода товара. Только подсказка — продажа с
 * такой ценой не блокируется. Без цены или без закупок (`lastPurchasePrice` пуст) сравнивать не с чем. */
export function isBelowPurchasePrice(line: Pick<EditorLine, 'unitPrice' | 'lastPurchasePrice'>): boolean {
  if (!line.unitPrice || !line.lastPurchasePrice) return false;
  return dec(line.unitPrice).lt(dec(line.lastPurchasePrice));
}

/** §4: «%» и «опт» связаны, источник — поле, которое менялось последним. */
export function recalcMarkup(line: EditorLine): EditorLine {
  if (!line.unitPrice) return line;
  const cost = dec(line.unitPrice);
  if (cost.isZero()) return line;
  if (line.lastEdited === 'markup' && line.markupPct) {
    return { ...line, wholesalePrice: wholesaleFromMarkup(cost, line.markupPct).toFixed(2) };
  }
  if (line.wholesalePrice) {
    const pct = markupPctFromPrices(cost, line.wholesalePrice);
    return { ...line, markupPct: pct ? pct.toFixed(2) : null };
  }
  return line;
}

/** Строка без товара: всё, что зависит от прежнего товара (цены, срок, основание возврата, партия,
 * факт инвентаризации, себестоимость), сбрасывается; остаётся только место строки в документе. */
function withoutProduct(line: EditorLine): EditorLine {
  return { ...emptyLine(line.lineNo), key: line.key, id: line.id, reason: line.reason };
}

/** §5: при выборе товара — P=N, коробок=1, цена=опт. категории (SALE/RETURN_IN — цена сразу).
 * Продажа: «Штук в коробке» и «Коробок» остаются пустыми — владелец вводит их сам, пустое P = N.
 * Если в строке уже был другой товар, его данные не переносятся на новый. */
export function applyCategoryDefaults(line: EditorLine, category: CategoryDto, type: EditorDocumentType): EditorLine {
  const changed = !!line.categoryId && line.categoryId !== category.id;
  const base: EditorLine = {
    ...(changed ? withoutProduct(line) : line),
    categoryId: category.id,
    categoryName: category.name,
    categoryPcsPerBox: category.pcsPerBox,
    archived: category.isArchived,
    stock: stockOf(category.stock),
    lastPurchasePrice: category.lastPurchasePrice ?? null,
    pcsPerBox: type === 'SALE' ? null : category.pcsPerBox,
  };
  if (type === 'SALE') {
    return { ...base, unitPrice: base.unitPrice ?? category.wholesalePrice };
  }
  if (type === 'RETURN_IN') {
    return { ...base, boxes: base.boxes ?? 1, unitPrice: base.unitPrice ?? category.wholesalePrice };
  }
  if (type === 'WRITE_OFF' || type === 'INVENTORY') {
    return { ...base, boxes: base.boxes ?? 1 };
  }
  // Приход (§2.1): пометки строки — быстрый переключатель пометок товара, поэтому строка
  // начинает с текущих; проведение запишет на товар то, что владелец в ней оставил.
  return { ...base, wholesalePrice: base.wholesalePrice ?? category.wholesalePrice, isNew: category.isNew, isPromo: category.isPromo };
}

/** Только что созданную в окне категории товар кладём в таблицу: в строку `preferredKey` (из неё
 * нажали «+»), если та ещё пустая; иначе в первую пустую; если пустых нет — в новую внизу. Заполненную
 * строку не затираем. Как и при выборе из списка, под заполненной последней строкой добавляется пустая. */
export function insertCreatedCategory(
  lines: EditorLine[],
  preferredKey: string | undefined,
  category: CategoryDto,
  type: EditorDocumentType,
): { lines: EditorLine[]; rowIndex: number } {
  const preferred = preferredKey ? lines.findIndex((l) => l.key === preferredKey && !l.categoryId) : -1;
  const firstEmpty = lines.findIndex((l) => !l.categoryId);
  const withTarget = preferred < 0 && firstEmpty < 0 ? [...lines, emptyLine(lines.length + 1)] : lines;
  const rowIndex = preferred >= 0 ? preferred : firstEmpty >= 0 ? firstEmpty : withTarget.length - 1;
  const filled = withTarget.map((l, i) => (i === rowIndex ? recalcMarkup(applyCategoryDefaults(l, category, type)) : l));
  return { lines: rowIndex === filled.length - 1 ? [...filled, emptyLine(filled.length + 1)] : filled, rowIndex };
}

/** §6.1 [ПРЕДЛ] «Заполнить по остаткам»: факт = текущий учёт, для строк с выбранным товаром.
 * Товар в минусе даёт факт 0/0 — посчитать на полке отрицательное количество нельзя. */
export function fillFromStock(lines: EditorLine[]): EditorLine[] {
  return lines.map((l) =>
    l.categoryId && l.stock
      ? { ...l, countedBoxes: Math.max(0, l.stock.sealedBoxes), countedLoose: toQtyString(Decimal.max(0, qty(l.stock.loosePcs))) }
      : l,
  );
}

/** INVENTORY (§6.1 [ИЗМ]): «Разница, шт» = факт − учёт, знаковая. */
export function inventoryDiff(line: EditorLine): Qty | null {
  if (line.countedBoxes == null || line.countedLoose == null || !line.stock || !line.pcsPerBox || qty(line.pcsPerBox).isZero()) return null;
  const factTotal = qty(line.pcsPerBox).times(line.countedBoxes).plus(qty(line.countedLoose));
  return toQtyString(factTotal.minus(qty(line.stock.qtyTotal)));
}

export function totals(lines: EditorLine[]): { positions: number; pcs: Qty; amount: string } {
  const withCategory = lines.filter((l) => l.categoryId);
  const pcs = withCategory.reduce((sum, l) => sum.plus(qtyOf(l)), new Decimal(0));
  const amount = withCategory.reduce((sum, l) => sum.plus(dec(amountOf(l))), dec(0));
  return { positions: withCategory.length, pcs: toQtyString(pcs), amount: amount.toFixed(2) };
}

export interface FooterTotals {
  positions: number;
  /** `null` — у документа нет «штук» в привычном смысле (инвентаризация задаёт остаток). */
  pcs: Qty | null;
  amountLabel: string;
  /** `null` — сумма появится после проведения: её считает склад по себестоимости. */
  amount: string | null;
}

/**
 * Итоги под строками. У списания цены нет, его сумма — потеря по себестоимости (§11.4), у
 * инвентаризации — потеря по недостаче (§6.1): обе считает склад при проведении, поэтому берутся
 * из строк сохранённого документа, а не из «цена × количество» (это всегда 0.00).
 */
export function footerTotals(type: EditorDocumentType, lines: EditorLine[], isPosted: boolean): FooterTotals {
  const base = totals(lines);
  if (type !== 'WRITE_OFF' && type !== 'INVENTORY') {
    return { positions: base.positions, pcs: base.pcs, amountLabel: 'Сумма', amount: base.amount };
  }
  const withCategory = lines.filter((l) => l.categoryId);
  const cost = withCategory.reduce((sum, l) => sum.plus(dec(l.costAmount ?? '0')), dec(0)).toFixed(2);
  if (type === 'INVENTORY') {
    return { positions: base.positions, pcs: null, amountLabel: 'Потеря по недостаче', amount: isPosted ? cost : null };
  }
  const costKnown = isPosted && withCategory.every((l) => l.costAmount !== null);
  return { positions: base.positions, pcs: base.pcs, amountLabel: 'Сумма потери', amount: costKnown ? cost : null };
}

/** Ячейки строки «Итого» под сеткой строк документа — по ключам столбцов (`qty`, `amount`, `cost`).
 *  Деньги — только там, где столбец есть в сетке; инвентаризация денег в сетке не показывает (её потеря
 *  стоит в футере), и без строк с товаром итога нет. `cost` — «—», пока склад не посчитал себестоимость. */
export interface GridTotalCells {
  qty: Qty | null;
  amount?: string;
  cost?: string;
}

export function gridTotalCells(type: EditorDocumentType, lines: EditorLine[], isPosted: boolean): GridTotalCells | null {
  if (type === 'INVENTORY') return null;
  const withCategory = lines.filter((l) => l.categoryId);
  if (withCategory.length === 0) return null;
  const footer = footerTotals(type, lines, isPosted);
  if (type === 'WRITE_OFF') return { qty: footer.pcs, cost: footer.amount ?? '—' };
  const costKnown = withCategory.every((l) => l.costAmount != null);
  const cells: GridTotalCells = { qty: footer.pcs, amount: footer.amount ?? undefined };
  if (type !== 'PURCHASE') cells.cost = costKnown ? sumMoney(withCategory.map((l) => l.costAmount)) : '—';
  return cells;
}

export interface StockShortage {
  categoryName: string;
  /** Сколько штук товара расходует документ (все его строки с этим товаром). */
  requested: Qty;
  /** Сколько штук доступно документу: остаток на складе плюс то, что он сам уже списал. */
  available: Qty;
  shortage: Qty;
}

/** Штуки по товарам в проведённом документе: остаток на складе уже уменьшен на них, поэтому при
 * правке проведённого расхода они прибавляются к доступному количеству. */
export function postedQtyByCategory(doc: Pick<DocumentDto, 'status' | 'deletedAt' | 'lines'> | undefined): Map<string, Qty> {
  const result = new Map<string, Qty>();
  if (!doc || doc.status !== 'POSTED' || doc.deletedAt) return result;
  for (const line of doc.lines) {
    if (line.categoryId) result.set(line.categoryId, toQtyString(qty(result.get(line.categoryId)).plus(qty(line.qtyPcs))));
  }
  return result;
}

/**
 * §5 «продавать больше остатка можно, система покажет предупреждение»: подсказка при вводе, до
 * проведения. Считается по товару, а не по строке: две строки по 6 шт при остатке 10 — это минус.
 * Только для расхода (продажа, списание, возврат поставщику) и только там, где остаток известен.
 */
export function stockShortages(type: EditorDocumentType, lines: EditorLine[], postedQty: ReadonlyMap<string, Qty>): Map<string, StockShortage> {
  const result = new Map<string, StockShortage>();
  if (type !== 'SALE' && type !== 'WRITE_OFF' && type !== 'RETURN_OUT') return result;
  const requested = new Map<string, Decimal>();
  for (const line of lines) {
    if (line.categoryId) requested.set(line.categoryId, (requested.get(line.categoryId) ?? new Decimal(0)).plus(qtyOf(line)));
  }
  for (const [categoryId, wanted] of requested) {
    const line = lines.find((l) => l.categoryId === categoryId && l.stock);
    if (!line?.stock) continue;
    const available = qty(line.stock.qtyTotal).plus(qty(postedQty.get(categoryId)));
    if (wanted.gt(available)) {
      result.set(categoryId, {
        categoryName: line.categoryName ?? '',
        requested: toQtyString(wanted),
        available: toQtyString(available),
        shortage: toQtyString(wanted.minus(available)),
      });
    }
  }
  return result;
}

export interface WindowWarnings {
  texts: string[];
  /** Номера строк, которые нужно подсветить. */
  lineNos: Set<number>;
}

/**
 * Предупреждения окна документа: пришедшие с сервера (состояние сохранённого документа) и
 * подсказка о минусе по введённым количествам. Архивный товар здесь не повторяется — у него своё,
 * блокирующее сообщение; серверное «уходит в минус» уступает подсказке с числами.
 */
export function windowWarnings(type: EditorDocumentType, lines: EditorLine[], server: WarningEntry[], postedQty: ReadonlyMap<string, Qty>): WindowWarnings {
  const shortages = stockShortages(type, lines, postedQty);
  const lineOf = (lineNo: number): EditorLine | undefined => lines.find((l) => l.lineNo === lineNo && l.categoryId);
  const kept = server.filter((w) => {
    if (w.code === 'ARCHIVED_CATEGORY') return false;
    const categoryId = lineOf(w.lineNo)?.categoryId;
    return !(w.code === 'STOCK_NEGATIVE' && categoryId && shortages.has(categoryId));
  });
  const hints = [...shortages.values()].map(
    (s) => `«${s.categoryName}»: остаток уйдёт в минус на ${s.shortage} шт — в документе ${s.requested} шт, доступно ${s.available} шт`,
  );
  const lineNos = new Set<number>(kept.filter((w) => w.lineNo > 0).map((w) => w.lineNo));
  for (const line of lines) {
    if (line.categoryId && shortages.has(line.categoryId)) lineNos.add(line.lineNo);
  }
  return { texts: [...describeWarnings(kept, (lineNo) => lineOf(lineNo)?.categoryName), ...hints], lineNos };
}

/**
 * Нужно ли пересеять окно пришедшим с сервера документом. Другой документ — всегда; тот же —
 * только если данные не старее уже показанных (кэш запроса может отстать от ответа только что
 * выполненного действия) и владелец не держит несохранённых правок.
 */
export function shouldReseed(seeded: { id: string; rowVersion: number } | undefined, incoming: { id: string; rowVersion: number }, dirty: boolean): boolean {
  if (!seeded || seeded.id !== incoming.id) return true;
  if (incoming.rowVersion < seeded.rowVersion) return false;
  return !dirty;
}

/** Пересев не должен пересоздавать поля ввода: строки с прежним номером сохраняют свой ключ. */
export function reuseKeys(previous: EditorLine[], next: EditorLine[]): EditorLine[] {
  const keyByLineNo = new Map(previous.map((l) => [l.lineNo, l.key]));
  return next.map((l) => ({ ...l, key: keyByLineNo.get(l.lineNo) ?? l.key }));
}

/** §7: направление кассового ордера, который логично создать из этого документа кнопкой
 * «Создать приходный/расходный ордер» (по образцу «Создать возврат») — SALE/RETURN_OUT получают
 * деньги (ПКО), PURCHASE/RETURN_IN платят (РКО). У WRITE_OFF/INVENTORY нет контрагента. */
export function cashOrderTargetType(type: EditorDocumentType): 'CASH_IN' | 'CASH_OUT' | null {
  if (type === 'SALE' || type === 'RETURN_OUT') return 'CASH_IN';
  if (type === 'PURCHASE' || type === 'RETURN_IN') return 'CASH_OUT';
  return null;
}

export function toLineInput(line: EditorLine, type: EditorDocumentType): DocumentLineInput {
  if (type === 'INVENTORY') {
    return {
      id: line.id,
      lineNo: line.lineNo,
      categoryId: line.categoryId,
      countedBoxes: line.countedBoxes ?? undefined,
      countedLoose: line.countedLoose ?? undefined,
      isNew: line.isNew,
      isPromo: line.isPromo,
    };
  }
  return {
    id: line.id,
    lineNo: line.lineNo,
    categoryId: line.categoryId,
    boxes: line.boxes ?? undefined,
    pcsPerBox: line.pcsPerBox ?? undefined,
    unitPrice: type === 'WRITE_OFF' ? undefined : (line.unitPrice ?? undefined),
    ...(type === 'PURCHASE'
      ? {
          wholesalePrice: line.wholesalePrice ?? undefined,
          markupPct: line.markupPct ?? undefined,
          lastEdited: line.lastEdited,
          expiryDate: line.expiryDate ?? undefined,
        }
      : {}),
    ...(type === 'RETURN_IN' || type === 'RETURN_OUT' ? { baseLineId: line.baseLineId } : {}),
    ...(type === 'WRITE_OFF' ? { reason: line.reason ?? undefined, batchId: line.batchId ?? undefined } : {}),
    isNew: line.isNew,
    isPromo: line.isPromo,
  };
}

/** §8.1: WRITE_OFF не требует контрагента/цены, но требует причину; остальные типы — как раньше. */
export function isDocumentComplete(type: EditorDocumentType, header: { docDate: string; counterpartyId?: string }, lines: EditorLine[]): boolean {
  if (!header.docDate) return false;
  if (type !== 'WRITE_OFF' && type !== 'INVENTORY' && !header.counterpartyId) return false;
  const withCategory = lines.filter((l) => l.categoryId);
  if (withCategory.length === 0) return false;
  if (type === 'INVENTORY') {
    return withCategory.every((l) => l.countedBoxes != null && l.countedBoxes >= 0 && l.countedLoose != null && !qty(l.countedLoose).isNegative());
  }
  return withCategory.every((l) => {
    // Продажа: пустое «Штук в коробке» = целая коробка, достаточно N товара.
    const pcsPerBox = type === 'SALE' ? (l.pcsPerBox ?? l.categoryPcsPerBox) : l.pcsPerBox;
    if (!l.categoryId || !l.boxes || l.boxes < 1 || !pcsPerBox || !qty(pcsPerBox).gt(0)) return false;
    if (type === 'WRITE_OFF') return !!l.reason;
    return l.unitPrice !== null;
  });
}

/**
 * Почему документ сейчас нельзя провести — текст для подсказки на недоступной кнопке «Провести»
 * и для сообщения при нажатии горячей клавиши; `null`, если можно. Решение («полон ли документ»)
 * принимает `isDocumentComplete`, а здесь только подбираются слова: `null` ⇔ документ полон.
 */
export function getPostBlockReason(type: EditorDocumentType, header: { docDate: string; counterpartyId?: string }, lines: EditorLine[]): string | null {
  if (!header.docDate) return 'Укажите дату документа';
  if (type !== 'WRITE_OFF' && type !== 'INVENTORY' && !header.counterpartyId) return 'Укажите контрагента';
  const withCategory = lines.filter((l) => l.categoryId);
  if (withCategory.length === 0) return 'Добавьте хотя бы одну строку с товаром';
  const incomplete = withCategory.find((l) => !isDocumentComplete(type, header, [l]));
  if (incomplete) return `Строка ${incomplete.lineNo}: ${incompleteLineHint(type, incomplete)}`;
  return isDocumentComplete(type, header, lines) ? null : 'Заполните обязательные поля';
}

/** Что не так в строке — слева направо по колонкам; подсказка, а не проверка (проверяет
 *  `isDocumentComplete`), поэтому при нескольких пустых полях называет одно. */
function incompleteLineHint(type: EditorDocumentType, line: EditorLine): string {
  if (type === 'INVENTORY') return 'укажите факт — коробки и россыпь';
  if (!line.boxes || line.boxes < 1) return 'укажите число коробок';
  if (type === 'WRITE_OFF') return line.reason ? 'укажите штук в коробке' : 'укажите причину списания';
  return line.unitPrice === null ? 'укажите цену' : 'укажите штук в коробке';
}

/** `newLabel` уже согласован по роду с «Новый/Новая/Новое» — не собирается шаблоном. */
const TYPE_NAME: Record<EditorDocumentType, { newLabel: string; prefix: string }> = {
  PURCHASE: { newLabel: 'Новый приход', prefix: 'Приход' },
  SALE: { newLabel: 'Новая продажа', prefix: 'Продажа' },
  RETURN_IN: { newLabel: 'Новый возврат от клиента', prefix: 'Возврат от клиента' },
  RETURN_OUT: { newLabel: 'Новый возврат поставщику', prefix: 'Возврат поставщику' },
  WRITE_OFF: { newLabel: 'Новое списание', prefix: 'Списание' },
  INVENTORY: { newLabel: 'Новая инвентаризация', prefix: 'Инвентаризация' },
};

export function windowTitle(type: EditorDocumentType, doc: DocumentDto | undefined, isNew: boolean, copyOfNumber?: string): string {
  const { newLabel, prefix } = TYPE_NAME[type];
  if (copyOfNumber) return `${newLabel} (копия ${copyOfNumber})`;
  if (isNew || !doc) return newLabel;
  const num = `${doc.number}/${doc.numberYear}`;
  return `${prefix} №${num}${statusLabel(doc)}`;
}

/** Удалённый документ хранит прежний статус — в заголовке важнее то, что он удалён. */
function statusLabel(doc: DocumentDto): string {
  if (doc.deletedAt) return ' (удалён)';
  if (doc.status === 'POSTED') return ' (проведён)';
  if (doc.status === 'CANCELLED') return ' (отменён)';
  return '';
}

/** Отказ проведения из-за архивного товара (422 CATEGORY_ARCHIVED): какие строки и какой товар.
 * Номера строк — из ответа сервера (`lines[N].categoryId`), названия — из строк окна. */
export function archivedLinesMessage(details: { field?: string }[] | undefined, lines: EditorLine[]): string | null {
  const lineNos = (details ?? [])
    .map((d) => /^lines\[(\d+)\]/.exec(d.field ?? '')?.[1])
    .filter((v): v is string => v !== undefined)
    .map(Number);
  if (lineNos.length === 0) return null;
  const parts = lineNos.map((lineNo) => {
    const name = lines.find((l) => l.lineNo === lineNo)?.categoryName;
    return name ? `строка ${lineNo} — «${name}»` : `строка ${lineNo}`;
  });
  return `Товар в архиве: ${parts.join(', ')}. Замените его или верните из архива — и проведите документ снова`;
}

/** После сохранения: строкам, которые ушли на сервер без id (новые), проставить id из ответа —
 * сервер сохраняет номера строк такими, какими их прислали. Иначе следующее сохранение сочло бы
 * их новыми и пересоздало. */
export function adoptLineIds(lines: EditorLine[], saved: DocumentLineDto[]): EditorLine[] {
  const idByLineNo = new Map(saved.map((l) => [l.lineNo, l.id]));
  return lines.map((l) => (l.categoryId && !l.id && idByLineNo.has(l.lineNo) ? { ...l, id: idByLineNo.get(l.lineNo) } : l));
}
